<template>
  
  <div class="form-container">
    <h1 class="page-title">顧客用予定表の編集</h1>

    <div class="mb-2">
      <!-- <button
        class="btn btn-sm me-2"
        :class="lang === 'ja' ? 'btn-primary' : 'btn-outline-primary'"
        @click="lang = 'ja'"
      >
        日本語
      </button>
      <button
        class="btn btn-sm"
        :class="lang === 'en' ? 'btn-primary' : 'btn-outline-primary'"
        @click="lang = 'en'"
      >
        English
      </button> -->
    </div>

    <CalendarEditJa
      v-if="lang === 'ja'"
      :year="year"
      :month="month"
      :customer-schedule-data="customerScheduleData"
      @update-start-time="updateStartTime"
      @update-end-time="updateEndTime"
      @go-back="goBack"
      @submit="updateCustomerSchedule"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import {
  getCustomerCalendar,
  updateCustomerCalendar
} from '@/api/axios';
import CalendarEditJa from './CalendarEditJa.vue';

export default {
  components: {
    CalendarEditJa
  },
  setup() {
    const title = ref('顧客予定表新規作成');

    watchEffect(() => {
      document.title = title.value;
    });

    const userId = ref('');
    const userName = ref('');
    const lang = ref('ja');
    const customer = ref({});
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    const errorMessages = ref({});

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const customerScheduleId = route.params.id
    const year = route.params.year;
    const month = route.params.month;
    const today = new Date().getDate();

    const customerScheduleData = ref({
      id: customerScheduleId,
      work_year: '',
      work_month: '',
      m_building_id: '',
      destination_address: 'ご担当者様',
      special_notes: '',
      m_customer_id: '',
      customer_nm: '',
      t_annual_work_monthly_calendar_details: []
    });

    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');

      try {
        const [
          workDataRes
        ] = await Promise.all([
          getCustomerCalendar(customerScheduleId, year, month)
        ]);

        // --- 予定データ ---
        const response = workDataRes;
        customerScheduleData.value = response.data.customer_schedule;

        
        // 建物IDでソート
        sortBillingsByBuilding();

      } catch (error) {
        console.error('予定情報またはマスタ情報の取得に失敗しました:', error);
      }
    };

    // 言語変更を監視して billing_lang_type を更新
    // watch(lang, (newLang) => {
    //   customerScheduleData.value.billing_lang_type = newLang === 'en' ? 1 : 0;
    // });

    // グループ情報を計算する：どの建物IDが何行あるか、どのindexで出てくるかをまとめる
    const groupedBillings = computed(() => {
      const grouped = {};

      // 請求詳細を1件ずつ処理
      customerScheduleData.value.t_annual_work_monthly_calendar_details.forEach((calendarDetail, index) => {
        const id = calendarDetail.m_building_id;

        // 初めて見た建物IDなら初期化
        if (!grouped[id]) {
          grouped[id] = {
            count: 0,       // 同じ建物IDの出現数
            indexes: []     // 各出現位置（index）
          };
        }

        // 数を1つ増やし、indexを記録
        grouped[id].count += 1;
        grouped[id].indexes.push(index);
      });

      return grouped;
    });

    // 建物順に並び替えておく（rowspan表示が連続になるように）
    const sortBillingsByBuilding = () => {
      customerScheduleData.value.t_annual_work_monthly_calendar_details.sort((a, b) => {
        if (a.m_building_id === b.m_building_id) {
          return a.sort_no - b.sort_no;
        }
        return a.m_building_id - b.m_building_id;
      });
    };

    // 日付変更を監視して曜日を更新
    watch(
      () => customerScheduleData.value.t_annual_work_monthly_calendar_details,
      (newDetails) => {
        newDetails.forEach((schedule) => {
          if (schedule.work_plan_dt) {
            const date = new Date(schedule.work_plan_dt);
            schedule.weekday = weekdays[date.getDay()];
          } else {
            schedule.weekday = '';
          }
        });
      },
      { deep: true }
    );

    const extractTimeString = (timeValue) => {
      if (!timeValue) return null;

      if (typeof timeValue === 'string') {
        if (/^\d{2}:\d{2}$/.test(timeValue)) return timeValue;
        if (timeValue.includes('T') && timeValue.length >= 16) return timeValue.substring(11, 16);
        return null;
      }

      if (typeof timeValue === 'object') {
        const hour = timeValue.HH ?? timeValue.hh ?? timeValue.hours ?? timeValue.hour;
        const minute = timeValue.mm ?? timeValue.minutes ?? timeValue.minute;

        if (hour != null && minute != null) {
          return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        }
      }

      return null;
    };

    const normalizeWorkPlanTime = (timeValue) => {
      const timeString = extractTimeString(timeValue);
      return timeString ? `2024-01-01T${timeString}:00.000+09:00` : null;
    };

    // 開始時間更新関数
    const updateStartTime = (newTime, scIndex) => {
      customerScheduleData.value.t_annual_work_monthly_calendar_details[scIndex].start_work_plan_time =
        normalizeWorkPlanTime(newTime);
    };

    // 終了時間更新関数
    const updateEndTime = (newTime, scIndex) => {
      customerScheduleData.value.t_annual_work_monthly_calendar_details[scIndex].end_work_plan_time =
        normalizeWorkPlanTime(newTime);
    };

    const updateCustomerSchedule = async (submittedScheduleData = customerScheduleData.value) => {
      try {
        const payload = {
          id: submittedScheduleData.id, // 更新対象のID
          work_year: submittedScheduleData.work_year,
          work_month: submittedScheduleData.work_month,
          m_customer_id: submittedScheduleData.m_customer_id,
          m_building_id: submittedScheduleData.m_building_id,
          destination_address: submittedScheduleData.destination_address,
          special_notes: submittedScheduleData.special_notes,
          t_annual_work_monthly_calendar_details: submittedScheduleData.t_annual_work_monthly_calendar_details.map((detail) => ({
            t_building_work_schedule_detail_id: detail.t_building_work_schedule_detail_id,
            m_building_id: detail.m_building_id,
            work_plan_dt: detail.work_plan_dt,
            weekday: detail.weekday,
            start_work_plan_time: normalizeWorkPlanTime(detail.start_work_plan_time),
            end_work_plan_time: normalizeWorkPlanTime(detail.end_work_plan_time),
            remarks: detail.remarks,
            sort_no: detail.sort_no,
          }))
        };

        await updateCustomerCalendar(customerScheduleId, payload);

        store.dispatch('setFlashMessage', { message: '顧客予定表更新完了！', type: 'success' });

        router.push({
          path: `/customer-calendar-detail/${customerScheduleId}/${year}/${month}`,
          query: { ...route.query, year, month }
        });
      } catch (error) {
        console.error('更新中にエラーが発生しました:', error);
      }
    };

    // 戻るボタンの挙動制御
    const goBack = () => {
      const { year, month, from } = route.query;

      if (from === 'list') {
        router.push({
          path: '/customer-calendars',
          query: { ...route.query, year, month }
        });
      } else if (from === 'detail') {
        router.push({
          path: `/customer-calendar-detail/${customerScheduleId}/${year}/${month}`,
          query: { ...route.query, year, month }
        });
      } else {
        // フォールバック（直接URLで編集画面開いたときなど）
        router.push({
          path: '/customer-calendars',
          query: { ...route.query, year, month }
        });
      }
    };

    onMounted(() => {
      fetchWorkScheduleDatas();
    });

    return {
      title,
      year,
      month,
      today,
      lang,
      customer,
      groupedBillings,
      updateStartTime,
      updateEndTime,
      errorMessages,
      customerScheduleData,
      updateCustomerSchedule,
      goBack
    };
  }
};
</script>
